import React from 'react';
import { INLINE_2 } from 'Components/frameworks/card/Card.constants';
import { ICONS } from 'Components/utils/icon/icons';
import { getKeyValue, t } from 'Src/i18n';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Button } from 'Components/frameworks/button/Button';
import { Card } from 'Components/frameworks/card/Card';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Title } from 'Components/frameworks/title/Title';
import { GreatMembersAccordion } from 'Components/widgets/great-members-accordion/GreatMembersAccordion';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
type Props = {
  className?: string;
  loyaltyProgram: Array<LoyaltyProgram>;
  loyaltyStatus: GreatMembersStatus | null | undefined;
};
export const GreatMembersStasusesSection = ({
  className,
  loyaltyProgram,
  loyaltyStatus,
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const loyaltyEnabled = getKeyValue('customerAccount.greatMember.stasusesSection.loyalty.enabled');
  const articleEnabled = getKeyValue('customerAccount.greatMember.stasusesSection.article.enabled');
  const tabsEnabled = getKeyValue('customerAccount.greatMember.stasusesSection.tabs.enabled');
  const faqEnabled = getKeyValue('customerAccount.greatMember.stasusesSection.faq.enabled');
  const allEnabled = articleEnabled && faqEnabled && loyaltyEnabled && tabsEnabled;
  return (
    <section className={classnames('space-y-60', className)}>
      <SiteContainer id="stasuses">
        {allEnabled && (
          <Title className="mb-40" hLevel={2} theme="title-brand-n0">
            {t('customerAccount.greatMember.stasusesSection.title')}
          </Title>
        )}
        {loyaltyEnabled && (
          <ul className="grid grid-cols-2 gap-y-40 sm:grid-cols-4">
            {loyaltyProgram.map(({ status, threshold }) => (
              <li
                className="flex flex-col items-center justify-center p-20 text-center odd:max-sm:border-r-1 sm:border-l-1 sm:first:border-l-0"
                key={threshold}
              >
                <Icon
                  className="mb-15 text-black"
                  isNotMonoChrome
                  name={ICONS[`CLUBMED_GREAT_MEMBERS_${status.toUpperCase()}_TRIDENT`]}
                  width="5.625rem"
                />
                <p className="sr-only">{status}</p>
                <p className="text-16 font-semibold">
                  {threshold > 0
                    ? t(
                        `customerAccount.greatMember.stasusesSection.tabs.${status}.pointsToReachStatus`,
                      )
                    : t('customerAccount.greatMember.stasusesSection.loyalty.noPointsLabel')}
                </p>
              </li>
            ))}
          </ul>
        )}
        {articleEnabled && (
          <Card
            className="mt-60"
            heading={{
              hLevel: 3,
              title: t('customerAccount.greatMember.stasusesSection.article.title'),
            }}
            layout={INLINE_2}
            media={{
              url: t('customerAccount.greatMember.stasusesSection.article.imageUrl'),
            }}
            text={{
              text: t('customerAccount.greatMember.stasusesSection.article.text'),
            }}
          />
        )}
      </SiteContainer>
      {tabsEnabled && (
        <section id="advantages">
          <SiteContainer className="mb-40">
            <Title hLevel={3} theme="title-brand-n2">
              {t('customerAccount.greatMember.stasusesSection.tabs.title')}
            </Title>
            <Title className="text-sienna" hLevel={4} theme="title-sans-n2">
              {t('customerAccount.greatMember.stasusesSection.tabs.subtitle')}
            </Title>
          </SiteContainer>
          <GreatMembersAccordion status={loyaltyStatus} />
        </section>
      )}
      {faqEnabled && (
        <SiteContainer className="text-center" id="faq">
          <p className="mb-15 font-brand text-24 font-bold">
            {t('customerAccount.greatMember.stasusesSection.faq.label')}
          </p>
          <Button
            onClick={() =>
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_CA_GM, {
                detail_click: 'see_the_faq',
              })
            }
            target="_blank"
            url={t('customerAccount.faq.link')}
          >
            {t('customerAccount.greatMember.stasusesSection.faq.cta')}
          </Button>
        </SiteContainer>
      )}
    </section>
  );
};
